import {Injectable, signal} from '@angular/core';
import {Container, Image} from '../model/layout.model';
import {DocumentModel} from '../model/document.model';
import {KiGrafikService} from './ki-grafik.service';

/**
 * Die Reihenfolge, in der die Redaktion die gelben Kästchen angehakt hat – **kartenübergreifend**
 * über das ganze Dokument (Oktober 2026).
 *
 * Jeder gesetzte gelbe Umschalter bekommt beim Anhaken die nächste freie Nummer (1, 2, 3 …);
 * wird er abgewählt, verschwindet seine Nummer und alle größeren rücken nach, damit die Zählung
 * lückenlos bleibt. Die Nummer erscheint als kleiner blauer Kreis unten links an der Box und
 * geht als Feld `order` in die KI-Anfrage – das Modell nennt die Regionen in genau dieser
 * Reihenfolge (`KiGrafikService`).
 *
 * **Zustand liegt im Dokument, nicht nur hier:** jede Nummer wird als fremder Schlüssel
 * `ki:order:<boxId>` in `image.values` der Karte abgelegt ({@link KiGrafikService.ORDER_PRAEFIX}),
 * damit sie mitgespeichert wird, die `ImageComponent` sie beim Spiegeln ihres Wert-Caches
 * durchreicht (Präfix `ki:`) und `KiGrafikService` sie ohne diesen Service lesen kann – auch für
 * Karten, die nie gerendert wurden. Dieser Service ist der einzige, der diese Schlüssel schreibt;
 * er hält die Karten des Dokuments, um beim Nachrücken auch nicht gerenderte Karten zu ändern.
 *
 * Die Map liegt in einem **Signal**, aus demselben Grund wie beim `KiErgebnisService`: die
 * Nummern vieler Karten hängen voneinander ab (Nachrücken), und ein Klick in der einen Karte muss
 * die Anzeige der anderen (OnPush) aktualisieren – ein im Template gelesenes Signal markiert alle
 * lesenden Ansichten.
 */
@Injectable({providedIn: 'root'})
export class KiReihenfolgeService {

  /** `<containerId>|<boxId>` -> Nummer, lückenlos ab 1. */
  private readonly nummern = signal<ReadonlyMap<string, number>>(new Map());

  /** Die Karten des geladenen Dokuments, damit das Nachrücken auch nicht gerenderte Karten erreicht. */
  private readonly karten = new Map<string, Image>();

  /** Die Nummer eines angehakten Kästchens, oder null. Liest das Signal – Templates aktualisieren sich. */
  nummer(containerId: string | undefined, boxId: string): number | null {
    return this.nummern().get(KiReihenfolgeService.schluessel(containerId, boxId)) ?? null;
  }

  /** Wie viele Kästchen nummeriert sind. */
  anzahl(): number {
    return this.nummern().size;
  }

  /** Kästchen angehakt: bekommt die nächste Nummer (nichts, wenn es schon eine hat). */
  anhaken(karte: Image, boxId: string): void {
    this.merkeKarte(karte);
    const key = KiReihenfolgeService.schluessel(karte.containerId, boxId);
    if (this.nummern().has(key)) {
      return;
    }
    const neu = new Map(this.nummern());
    neu.set(key, neu.size + 1);
    this.uebernehme(neu);
  }

  /** Kästchen abgewählt: Nummer weg, größere rücken nach. */
  abhaken(karte: Image, boxId: string): void {
    this.merkeKarte(karte);
    this.entferne([KiReihenfolgeService.schluessel(karte.containerId, boxId)]);
  }

  /** Alle Kästchen einer Karte abgewählt ("leeren"). */
  entferneKarte(karte: Image): void {
    this.merkeKarte(karte);
    const praefix = KiReihenfolgeService.schluessel(karte.containerId, '');
    this.entferne([...this.nummern().keys()].filter(key => key.startsWith(praefix)));
  }

  /**
   * Liest die gespeicherten Nummern eines geladenen Dokuments (`ki:order:<boxId>` an jeder
   * Karte) und merkt sich dessen Karten. Vorher wird alles verworfen – ein neues Dokument bringt
   * nichts mit. Lücken oder Dubletten aus älteren Datensätzen werden durch Nachrücken in der
   * gespeicherten Reihenfolge bereinigt.
   */
  ladeAusDokument(document: DocumentModel | null | undefined): void {
    this.karten.clear();
    const gefunden: {key: string; nummer: number}[] = [];
    this.durchlaufe(document?.tabs ?? [], karte => {
      this.merkeKarte(karte);
      Object.entries(karte.values ?? {}).forEach(([schluessel, wert]) => {
        if (schluessel.startsWith(KiGrafikService.ORDER_PRAEFIX)) {
          const nummer = Number.parseInt(wert, 10);
          if (Number.isFinite(nummer)) {
            gefunden.push({key: KiReihenfolgeService.schluessel(karte.containerId,
              schluessel.substring(KiGrafikService.ORDER_PRAEFIX.length)), nummer});
          }
        }
      });
    });
    gefunden.sort((a, b) => a.nummer - b.nummer);
    const neu = new Map<string, number>();
    gefunden.forEach(e => neu.set(e.key, neu.size + 1));
    this.uebernehme(neu);
  }

  /** Alles vergessen (Dokument geschlossen). */
  leeren(): void {
    this.karten.clear();
    this.uebernehme(new Map());
  }

  private entferne(keys: string[]): void {
    if (!keys.some(key => this.nummern().has(key))) {
      return;
    }
    const rest = [...this.nummern().entries()]
      .filter(([key]) => !keys.includes(key))
      .sort((a, b) => a[1] - b[1]);
    const neu = new Map<string, number>();
    rest.forEach(([key]) => neu.set(key, neu.size + 1));
    this.uebernehme(neu);
  }

  /** Setzt das Signal und schreibt die Nummern in `image.values` aller bekannten Karten. */
  private uebernehme(neu: Map<string, number>): void {
    const alt = this.nummern();
    this.nummern.set(neu);
    const betroffen = new Set<string>();
    [...alt.keys(), ...neu.keys()].forEach(key => {
      if (alt.get(key) !== neu.get(key)) {
        betroffen.add(key.substring(0, key.indexOf('|')));
      }
    });
    betroffen.forEach(containerId => this.schreibe(this.karten.get(containerId), containerId, neu));
  }

  /** Schreibt die Nummern einer Karte als `ki:order:<boxId>` in deren `values` (und räumt alte weg). */
  private schreibe(karte: Image | undefined, containerId: string, nummern: ReadonlyMap<string, number>): void {
    if (!karte) {
      return;
    }
    const werte: {[key: string]: string} = {};
    Object.entries(karte.values ?? {}).forEach(([key, wert]) => {
      if (!key.startsWith(KiGrafikService.ORDER_PRAEFIX)) {
        werte[key] = wert;
      }
    });
    const praefix = KiReihenfolgeService.schluessel(containerId, '');
    nummern.forEach((nummer, key) => {
      if (key.startsWith(praefix)) {
        werte[KiGrafikService.ORDER_PRAEFIX + key.substring(praefix.length)] = String(nummer);
      }
    });
    karte.values = werte;
  }

  private merkeKarte(karte: Image): void {
    if (karte.containerId) {
      this.karten.set(karte.containerId, karte);
    }
  }

  private durchlaufe(container: Container[], besuch: (karte: Image) => void): void {
    for (const kind of container) {
      if (kind.containerType === 'Image') {
        besuch(kind as Image);
      }
      this.durchlaufe(kind.containerList ?? [], besuch);
    }
  }

  private static schluessel(containerId: string | undefined, boxId: string): string {
    return `${containerId ?? ''}|${boxId}`;
  }
}
